import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/components/ui/useToast';
import { apiError } from '@/lib/apiError';
import { alterTrade, createTrade, type TradeInput, type TradeItem, type VoucherFull } from '@/api/accountsBooks';

/** Helpers the Sales and Purchase forms share. */

export type Line = TradeItem & { key: number };
let seq = 1;
export const newLine = (): Line => ({ key: seq++, itemId: null, name: '', hsn: '', qty: 1, unit: 'Pcs', rate: 0, disc: 0, gst: 5 });
export const fromItems = (items: TradeItem[]): Line[] => items.map((i) => ({ ...i, key: seq++ }));

/** YYYY-MM-DD plus n days. */
export const addDays = (d: string, n: number) => (d ? new Date(Date.parse(`${d}T00:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10) : '');

/** Saves (or alters) a Sales / Purchase voucher, refreshes the Accounts screens and closes the form. */
export function useSaveTrade(type: 'Sales' | 'Purchase', voucher: VoucherFull | null, onClose: () => void) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (body: TradeInput) => (voucher ? alterTrade(voucher.id, body) : createTrade(type, body)),
    onSuccess: (v) => {
      toast.push(`${type} ${v.voucherNo} ${voucher ? 'altered' : 'saved'}`, 'success');
      qc.invalidateQueries({ queryKey: ['acc-books'] });
      onClose();
    },
    onError: (e) => toast.push(apiError(e, 'Save failed'), 'error'),
  });
}
